/** Task CRUD operations against the /api/tasks endpoints. */

import { del, get, patch, post } from "@/services/api-client";
import { ERASE_TIMEOUT_MS } from "@/services/write-deadline";
import type {
  PaginatedTaskList,
  TaskCreate,
  TaskResponse,
  TaskSummary,
  TaskUpdate,
} from "@patent-studio/api-types";

export interface ListTasksParams {
  status?: string;
  tag?: string;
  q?: string;
  /** One client's drafts. Read beside the caller, so another's client lists nothing. */
  client_id?: string;
  /** The drafts made before clients existed, which sit under none. */
  without_client?: boolean;
  /** Recently deleted is left out unless asked for: `only` lists it, `include` adds it. */
  deleted?: "only" | "include";
  skip?: number;
  limit?: number;
  sort?: string;
}

function buildQuery(params: ListTasksParams): string {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.tag) qs.set("tag", params.tag);
  if (params.q) qs.set("q", params.q);
  if (params.client_id) qs.set("client_id", params.client_id);
  if (params.without_client) qs.set("without_client", "true");
  if (params.deleted) qs.set("deleted", params.deleted);
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.sort) qs.set("sort", params.sort);
  const str = qs.toString();
  return str ? `?${str}` : "";
}

export function createTask(body: TaskCreate): Promise<TaskResponse> {
  return post<TaskResponse>("/tasks", body);
}

export function listTasks(
  params: ListTasksParams = {},
): Promise<PaginatedTaskList> {
  return get<PaginatedTaskList>(`/tasks${buildQuery(params)}`);
}

export function getTask(taskId: string): Promise<TaskResponse> {
  return get<TaskResponse>(`/tasks/${taskId}`);
}

export function updateTask(
  taskId: string,
  body: TaskUpdate,
): Promise<TaskResponse> {
  return patch<TaskResponse>(`/tasks/${taskId}`, body);
}

/** Moves a draft to Recently deleted, from where it can be restored. */
export function trashTask(taskId: string): Promise<TaskSummary> {
  return post<TaskSummary>(`/tasks/${taskId}/trash`);
}

/** Returns a draft from Recently deleted to the list its status puts it in. */
export function restoreTask(taskId: string): Promise<TaskSummary> {
  return post<TaskSummary>(`/tasks/${taskId}/restore`);
}

/** Removes a draft in Recently deleted for good. */
export function deleteTask(taskId: string): Promise<void> {
  return del(`/tasks/${taskId}`, { deadlineMs: ERASE_TIMEOUT_MS });
}
